// @ds/primitives/Link — universal link with closed-enum kind enum and
// protocol-appropriate icon. Covers tel, mailto, website, external, file,
// and image link protocols.
//
// Rendering strategy:
//   - Uses the same CSS-injection pattern as ExternalLink (no inline style).
//   - Icon is a leading AntD icon glyph; all icon imports route through this
//     file (swap-in-one-place rule).
//   - Text is rendered via TextValue so all size/tone/mono/tabular axes
//     apply consistently.
//
// Rule 5 — AntD wrap rule satisfied: we import @ant-design/icons directly
// here so swapping the icon library is one-file work.

import React, { type ReactNode } from "react";
import {
  DownloadOutlined,
  LinkOutlined,
  MailOutlined,
  PhoneOutlined,
  PictureOutlined,
} from "@ant-design/icons";
import { TextValue } from "@ds/display/TextValue";
import { Inline } from "@ds/layout/Inline";
import { FONT_SIZE, MOTION, TONE, TEXT } from "@ds/tokens/palette";

// ─── Enum ─────────────────────────────────────────────────────────────────

export type LinkKind =
  | "tel"       // href=tel:..., mono + tabular numerals, phone icon
  | "mailto"    // href=mailto:..., envelope icon
  | "website"   // href=https://..., link icon, target=_blank rel=noopener
  | "file"      // href=blob/url, download icon, download attr optional
  | "image"     // href=url, picture icon, opens in new tab
  | "external"; // generic external href, link icon, target=_blank

// ─── Inputs ───────────────────────────────────────────────────────────────

export interface LinkInput {
  kind: LinkKind;
  /** Full href — for tel/mailto include the scheme: "tel:+48…", "mailto:…". */
  href: string;
  /**
   * Display text.
   * For `kind="tel"` / `kind="mailto"` defaults to the href body
   * (everything after the scheme), so callers can pass just the href.
   */
  children?: ReactNode;
  /** Show the protocol icon. Default `true`. */
  showIcon?: boolean;
  /**
   * Tone — controls the text color.
   * - `"primary"` (default) → `TONE.blue.text`
   * - `"secondary"` → `TEXT.secondary`
   * - `"faint"` → `TEXT.faint`
   */
  tone?: "primary" | "secondary" | "faint";
  /** Font size passed to TextValue. Default `"sm"`. */
  size?: "xs" | "sm" | "md" | "lg";
  /**
   * `download` attribute (meaningful only for `kind="file"`).
   * Pass `true` to trigger browser download with the original filename,
   * or a string to force a specific filename.
   */
  download?: string | boolean;
  testId?: string;
}

export type LinkProps = LinkInput;

// ─── Styles (CSS injection — no inline style on the <a> tag) ─────────────

const CSS_ID = "__ds-link";

function ensureCSS(): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(CSS_ID)) return;
  const el = document.createElement("style");
  el.id = CSS_ID;
  el.textContent = [
    `.ds-link { text-decoration: none; cursor: pointer; transition: opacity ${MOTION.fast} ease; }`,
    `.ds-link:hover { text-decoration: underline; opacity: 0.85; }`,
    `.ds-link--primary { color: ${TONE.blue.text}; }`,
    `.ds-link--secondary { color: ${TEXT.secondary}; }`,
    `.ds-link--faint { color: ${TEXT.faint}; }`,
  ].join("\n");
  document.head.appendChild(el);
}

// ─── Internal helpers ─────────────────────────────────────────────────────

function iconForKind(kind: LinkKind): ReactNode {
  switch (kind) {
    case "tel":      return <PhoneOutlined   style={{ fontSize: FONT_SIZE.body }} />;
    case "mailto":   return <MailOutlined    style={{ fontSize: FONT_SIZE.body }} />;
    case "image":    return <PictureOutlined style={{ fontSize: FONT_SIZE.body }} />;
    case "file":     return <DownloadOutlined style={{ fontSize: FONT_SIZE.body }} />;
    case "website":
    case "external": return <LinkOutlined    style={{ fontSize: FONT_SIZE.body }} />;
  }
}

function anchorProps(kind: LinkKind): Pick<React.AnchorHTMLAttributes<HTMLAnchorElement>, "target" | "rel"> {
  switch (kind) {
    case "website":
    case "external":
    case "image":
      return { target: "_blank", rel: "noopener noreferrer" };
    default:
      return {};
  }
}

/** Strip the scheme prefix for default display text on tel/mailto. */
function defaultContent(href: string, kind: LinkKind): string {
  if (kind === "tel"    && href.startsWith("tel:"))    return href.slice(4);
  if (kind === "mailto" && href.startsWith("mailto:")) return href.slice(7);
  return href;
}

// ─── Component ────────────────────────────────────────────────────────────

export function Link({
  kind,
  href,
  children,
  showIcon = true,
  tone = "primary",
  size = "sm",
  download,
  testId,
}: LinkProps) {
  ensureCSS();

  const className = `ds-link ds-link--${tone}`;
  const extra = anchorProps(kind);

  const displayChildren: ReactNode =
    children !== undefined ? children : defaultContent(href, kind);

  const isTel = kind === "tel";

  return (
    <a
      href={href}
      className={className}
      data-testid={testId}
      download={download}
      {...extra}
    >
      <Inline as="span" inline gap="xs" align="center">
        {showIcon && iconForKind(kind)}
        <TextValue
          as="span"
          size={size}
          mono={isTel}
          tabular={isTel}
        >
          {displayChildren}
        </TextValue>
      </Inline>
    </a>
  );
}
